import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isPresent } from '@ember/utils';
import { inject } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import * as pdfjsLib from 'pdfjs-dist';
import printJS from 'print-js-updated';
import { PDFDocument, EncryptedPDFError } from 'pdf-lib/dist/pdf-lib.esm.js';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div class=\'pdf-viewer-container\'>\n  {{#if this.loading}}\n    <div class=\'pdf-loading\'>\n      <div class=\'pdf-skeleton\' aria-label={{t \'loading pdf\'}} role=\'status\'>\n        <div class=\'pdf-skeleton__toolbar\'>\n          <span class=\'pdf-skeleton__chip\'></span>\n          <span class=\'pdf-skeleton__chip\'></span>\n          <span class=\'pdf-skeleton__chip\'></span>\n        </div>\n        <div class=\'pdf-skeleton__page\'>\n          <span class=\'pdf-skeleton__line\'></span>\n          <span class=\'pdf-skeleton__line\'></span>\n          <span class=\'pdf-skeleton__line\'></span>\n          <span class=\'pdf-skeleton__line\'></span>\n          <span class=\'pdf-skeleton__line\'></span>\n          <span class=\'pdf-skeleton__line\'></span>\n          <span class=\'pdf-skeleton__line\'></span>\n        </div>\n      </div>\n    </div>\n  {{/if}}\n\n  {{#if this.error}}\n    {{#if this.unreadableDocument}}\n      {{! The file itself is not a usable PDF: the <object> fallback is\n          pointless, the browser engine rejects the same bytes. Tell the user\n          and still offer the raw file so they can check or re-upload it. }}\n      <div class=\'pdf-error\' role=\'alert\' data-test-pdf-unreadable>\n        <p>\n          {{t \'pdf unreadable file message\'}}\n        </p>\n        {{#if @url}}\n          <a href={{@url}} target=\'_blank\' rel=\'noopener noreferrer\'>{{t \'download\'}}</a>\n        {{/if}}\n      </div>\n    {{else if this.fallbackMode}}\n      <object\n        type=\'application/pdf\'\n        data={{this.blobUrl}}\n        width=\'100%\'\n        style=\'height: min(800px, calc(100dvh - 120px))\'\n      >\n        <iframe src={{this.blobUrl}} class=\'full-height-2-row\'>\n          <p>\n            {{t \'Your browser do not support pdf preview\'}}\n            <a href={{this.blobUrl}} target=\'_blank\' rel=\'noopener noreferrer\'>\n              {{t \'download\'}}\n            </a>\n          </p>\n        </iframe>\n      </object>\n    {{else}}\n      <div class=\'pdf-error\' role=\'alert\' data-test-pdf-error>\n        <p>\n          {{t \'pdf error message\'}}\n        </p>\n        {{#if @url}}\n          <a href={{@url}} target=\'_blank\' rel=\'noopener noreferrer\'>{{t \'open pdf in new tab\'}}</a>\n        {{/if}}\n      </div>\n    {{/if}}\n  {{/if}}\n\n  {{#if this.canvasReady}}\n    <div class=\'pdf-controls\'>\n      <div class=\'control-block\'>\n        <button\n          class=\'link-button show-for-medium {{if this.zoomOutTask.isRunning \"loading\"}}\'\n          aria-label={{t \'zoom out\'}}\n          disabled={{this.zoomOutTask.isRunning}}\n          data-test-zoom-out\n          {{on \'click\' (perform this.zoomOutTask)}}\n        >\n          <FaIcon @icon=\'minus\' aria-hidden=\'true\' />\n        </button>\n\n        <button\n          class=\'link-button show-for-medium {{if this.zoomInTask.isRunning \"loading\"}}\'\n          aria-label={{t \'zoom in\'}}\n          disabled={{this.zoomInTask.isRunning}}\n          data-test-zoom-in\n          {{on \'click\' (perform this.zoomInTask)}}\n        >\n          <FaIcon @icon=\'plus\' aria-hidden=\'true\' />\n        </button>\n\n        <select\n          class=\'zoom-select\'\n          aria-label={{t \'zoom level\'}}\n          data-test-set-zoom-from-select\n          {{on \'change\' (perform this.setZoomFromSelectTask)}}\n        >\n          {{#each this.zoomOptions as |z|}}\n            <option value={{z}} selected={{eq this.zoomLevel z}}>{{z}}%</option>\n          {{/each}}\n        </select>\n\n        <div class=\'divider show-for-medium\'></div>\n      </div>\n      <div class=\'control-block\'>\n        <button\n          class=\'link-button {{if this.prevPageTask.isRunning \"loading\"}}\'\n          aria-label={{t \'previous page\'}}\n          disabled={{this.prevPageTask.isRunning}}\n          data-test-prev-page\n          {{on \'click\' (perform this.prevPageTask)}}\n        >\n          <FaIcon @icon=\'chevron-left\' aria-hidden=\'true\' />\n        </button>\n\n        <div class=\'page-selector\'>\n          <input\n            type=\'number\'\n            min=\'1\'\n            max={{this.totalPages}}\n            value={{this.page}}\n            aria-label={{t \'page number\'}}\n            data-test-go-to-page\n            {{on \'change\' (perform this.goToPageTask)}}\n          />\n          <span aria-hidden=\'true\'>\n            /\n          </span>\n          <span aria-label={{t \'of pages\' count=this.totalPages}}>\n            {{this.totalPages}}\n          </span>\n        </div>\n\n        <button\n          class=\'link-button {{if this.nextPageTask.isRunning \"loading\"}}\'\n          aria-label={{t \'next page\'}}\n          disabled={{this.nextPageTask.isRunning}}\n          data-test-next-page\n          {{on \'click\' (perform this.nextPageTask)}}\n        >\n          <FaIcon @icon=\'chevron-right\' aria-hidden=\'true\' />\n        </button>\n\n        <div class=\'divider\'></div>\n      </div>\n      <div class=\'control-block\'>\n        <button\n          class=\'link-button {{if this.rotatePdfTask.isRunning \"loading\"}}\'\n          aria-label={{t \'rotate\'}}\n          disabled={{this.rotatePdfTask.isRunning}}\n          data-test-rotate-pdf\n          {{on \'click\' (perform this.rotatePdfTask)}}\n        >\n          <FaIcon @icon=\'rotate-right\' aria-hidden=\'true\' />\n          <EmberTooltip>\n            {{t \'rotate\'}}\n          </EmberTooltip>\n        </button>\n\n        <button\n          class=\'link-button {{if this.downloadPdfTask.isRunning \"loading\"}}\'\n          aria-label={{t \'download\'}}\n          disabled={{this.downloadPdfTask.isRunning}}\n          data-test-download-pdf\n          {{on \'click\' (perform this.downloadPdfTask)}}\n        >\n          <FaIcon @icon=\'download\' aria-hidden=\'true\' />\n          <EmberTooltip>\n            {{t \'download\'}}\n          </EmberTooltip>\n        </button>\n\n        <button\n          class=\'link-button {{if this.printPdfTask.isRunning \"loading\"}}\'\n          aria-label={{t \'print\'}}\n          disabled={{this.printPdfTask.isRunning}}\n          data-test-print-pdf\n          {{on \'click\' (perform this.printPdfTask)}}\n        >\n          <FaIcon @icon=\'print\' aria-hidden=\'true\' />\n          <EmberTooltip>\n            {{t \'print\'}}\n          </EmberTooltip>\n        </button>\n      </div>\n    </div>\n  {{/if}}\n\n  <div class=\'pdf-canvas-wrapper\'>\n    <canvas\n      class={{if this.canvasReady \'\' \'pdf-canvas--hidden\'}}\n      id={{this.canvasId}}\n      style={{@style}}\n      {{did-insert this.registerCanvas}}\n      {{did-update this.handleUrlChange @url}}\n    ></canvas>\n  </div>\n</div>");

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec0, _dec1, _dec10, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12;
const CDN_WORKER_URL = 'https://beebeeassets.beebeeboard.com/assets/js/pdf.worker.5.4.54.min.mjs';
pdfjsLib.GlobalWorkerOptions.workerSrc = CDN_WORKER_URL;

// Print.js settles `printPdfTask` through `onLoadingEnd` / `onError`. Some
// browsers call NEITHER for a blob URL (Safari, or a blocked popup): without a
// deadline the promise never resolves, the task stays running forever, the
// spinner spins and — because the task is `drop` — every later print click is
// dropped as well, so the button stays dead for the rest of the session
// (POP-341). Eight seconds is far longer than a real iframe load and short
// enough that the user is not left guessing.
const PRINT_LOADING_TIMEOUT_MS = 8000;

// A fallback tab keeps using the blob URL after the task has ended, so the URL
// must survive the next revoke just like an in-flight print does.
const BLOB_URL_GRACE_MS = 60000;
function generateUniqueId(prefix = 'pdf-canvas') {
  return `${prefix}-${Math.random().toString(36).substring(2, 10)}`;
}
let BbPdfViewerComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _dec3 = task({
  enqueue: true
}), _dec4 = task({
  drop: true
}), _dec5 = task({
  drop: true
}), _dec6 = task({
  drop: true
}), _dec7 = task({
  drop: true
}), _dec8 = task({
  drop: true
}), _dec9 = task({
  drop: true
}), _dec0 = task({
  restartable: true
}), _dec1 = task({
  restartable: true
}), _dec10 = task({
  drop: true
}), _class = class BbPdfViewerComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "log", _descriptor, this);
    _initializerDefineProperty(this, "notify", _descriptor2, this);
    _initializerDefineProperty(this, "intl", _descriptor3, this);
    // Timestamp of the last successful fallback-tab open; keeps `_revokeBlobUrl`
    // from pulling the blob out from under that tab.
    this._printFallbackAt = 0;
    _initializerDefineProperty(this, "canvasElement", _descriptor4, this);
    _initializerDefineProperty(this, "pdf", _descriptor5, this);
    _initializerDefineProperty(this, "pdfBlob", _descriptor6, this);
    _initializerDefineProperty(this, "blobUrl", _descriptor7, this);
    // Blob con i form field appiattiti (appearance streams generati). Calcolato
    // pigramente al primo print/download e riusato finché non cambia il PDF.
    this._flattenedBlob = null;
    _initializerDefineProperty(this, "fallbackMode", _descriptor8, this);
    // Failure of the last loadPdfTask run. The task catches its own error (a
    // fire-and-forget perform must not reject), so ember-concurrency still sees
    // the run as successful: there is NO `task.error` on an ember-concurrency
    // Task, the previous getter read `undefined` on every render and the whole
    // error branch of the template was dead code — the user got a blank viewer
    // with no message at all (Sentry EMBER-MEDICAL-2XJ).
    _initializerDefineProperty(this, "loadError", _descriptor9, this);
    // True when the file itself is unusable (corrupt/truncated, or not a PDF at
    // all despite the .pdf name). The <object> fallback cannot help here: the
    // browser's own PDF engine rejects the very same bytes, so we show a message.
    _initializerDefineProperty(this, "unreadableDocument", _descriptor0, this);
    _initializerDefineProperty(this, "page", _descriptor1, this);
    _initializerDefineProperty(this, "totalPages", _descriptor10, this);
    _initializerDefineProperty(this, "zoomLevel", _descriptor11, this);
    _initializerDefineProperty(this, "rotation", _descriptor12, this);
    this.zoomOptions = [25, 50, 75, 90, 100, 110, 125, 150, 200, 300, 400, 500];
    this._currentRenderTask = null;
    this._resizeObserver = null;
    this.canvasId = generateUniqueId();
  }
  get loading() {
    return this.loadPdfTask.isRunning;
  }
  get error() {
    return this.loadError;
  }

  // Show canvas only once a page is rendered. pdf is set before loadPdfTask
  // finishes (loadPageTask is awaited inside it), so gating on pdf alone makes
  // the canvas appear under the still-visible skeleton, then jump up when the
  // skeleton is removed. Gate on !loading too to avoid the jump.
  get canvasReady() {
    return isPresent(this.pdf) && !this.loading;
  }
  willDestroy() {
    super.willDestroy();
    this._revokeBlobUrl(this.blobUrl);
    this._cancelCurrentRender();
    this._destroyPdfResources();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  // Releases a blob URL we own. A print in flight means print-js is still
  // loading that blob into its hidden iframe: revoking now aborts the load and
  // `iframe.contentWindow` is null when print-js calls `.print()` (Sentry
  // EMBER-MEDICAL-2SH), so defer instead. The URL is captured in the closure
  // because the caller may be tearing the component down.
  _revokeBlobUrl(url) {
    if (!url) return;
    const fallbackTabIsFresh = Date.now() - this._printFallbackAt < BLOB_URL_GRACE_MS;
    if (this.printPdfTask?.isRunning || fallbackTabIsFresh) {
      setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_GRACE_MS);
    } else {
      URL.revokeObjectURL(url);
    }
  }
  _cancelCurrentRender() {
    if (this._currentRenderTask) {
      this._currentRenderTask.cancel();
      this._currentRenderTask = null;
    }
  }

  // Restartable: se chiamato mentre è in corso, cancella e riparte da capo.
  // Risolve la race condition tra did-insert e did-update.
  *loadPdfTask() {
    // silent-guard-ok: render lifecycle (canvas not inserted / no document yet), not a user refusal
    if (!this.canvasElement || !isPresent(this.args.url)) return;
    this.fallbackMode = false;
    this.loadError = null;
    this.unreadableDocument = false;
    // Drop the previous document's bytes up front: they belong to the URL we
    // are leaving. Kept around, a failure below would hand the <object>
    // fallback the PREVIOUS file and silently display the wrong document.
    this.pdfBlob = null;
    this._flattenedBlob = null;
    this._cancelCurrentRender();
    this._destroyPdfResources();
    try {
      const response = yield fetch(this.args.url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = yield response.blob();
      this.pdfBlob = blob;
      this._flattenedBlob = null;
      const loadingTask = pdfjsLib.getDocument({
        data: yield blob.arrayBuffer()
      });
      // Kept on the instance so a restart/teardown can destroy it: without
      // destroy() every rapid URL change leaked a pdf.js worker + document.
      this._loadingTask = loadingTask;
      const pdf = yield loadingTask.promise;
      this.pdf = pdf;
      this.totalPages = pdf.numPages;
      this.page = 1;
      this.zoomLevel = 100;
      yield this.loadPageTask.perform();
    } catch (e) {
      // ember-concurrency cancels (it does not reject) a task whose host is
      // torn down, so this catch normally runs on a live component — guard
      // anyway before writing tracked state, since a truthy-but-destroyed
      // component still throws on a tracked set.
      if (this.isDestroyed || this.isDestroying) return;

      // Drop the pdf.js document before showing any error branch. The failure
      // can come from `loadPageTask` — i.e. AFTER `this.pdf` was set — and
      // `canvasReady` only looks at `pdf` + `loading`: leaving it set would
      // render the toolbar and canvas UNDERNEATH the fallback/error state.
      this._destroyPdfResources();

      // pdf.js names its own rejections. `InvalidPDFException` means the bytes
      // are not a usable PDF at all (corrupt upload, or another file type
      // renamed .pdf) and `PasswordException` means it is locked with a
      // password we do not have. Both are bad INPUT, not a defect of ours.
      const isUnreadable = e?.name === 'InvalidPDFException';
      const needsPassword = e?.name === 'PasswordException';
      if (isUnreadable || needsPassword) {
        // `info`, not `warn`: in production `log.warn` still reaches Sentry via
        // captureMessage, and a file the user uploaded broken is not actionable
        // for us — it would only be noise (Sentry EMBER-MEDICAL-2XJ). It is NOT
        // swallowed: the template now shows an explicit message for an
        // unreadable file, and the <object> fallback lets the browser prompt
        // for the password on a locked one, so the user is always told.
        this.log.info('PDF not displayable, unusable input file:', e);
      } else {
        // Network / HTTP / unknown failures stay error-level: those are ours.
        this.log.error('PDF loading failed:', e);
      }
      this.loadError = e;
      this.unreadableDocument = isUnreadable;

      // The <object> fallback needs a blob URL, and it is only worth showing
      // when we actually downloaded bytes the browser has a chance of
      // rendering. Rebuild the URL from THOSE bytes: `blobUrl` may still point
      // at a previous document (printPdfTask creates one too).
      if (!isUnreadable && this.pdfBlob instanceof Blob) {
        const staleUrl = this.blobUrl;
        this.blobUrl = URL.createObjectURL(this.pdfBlob);
        this._revokeBlobUrl(staleUrl);
        this.fallbackMode = true;
      }
      // The error reaches the template through `loadError`; we do not rethrow
      // it, to avoid unhandled rejections on fire-and-forget performs.
    }
  }

  // Releases the pdf.js document + loading task (worker) of the previous
  // load. Safe to call multiple times.
  _destroyPdfResources() {
    this._loadingTask?.destroy?.();
    this._loadingTask = null;
    this.pdf = null;
  }

  // Restartable: ogni nuova richiesta di render cancella quella precedente.
  *loadPageTask() {
    // silent-guard-ok: render lifecycle (document not loaded yet), not a user refusal
    if (!this.pdf || !this.canvasElement) return;
    this._cancelCurrentRender();
    const page = yield this.pdf.getPage(this.page);

    // The canvas can be detached while getPage resolves (route change,
    // panel close): parentElement would be null.
    if (!this.canvasElement?.parentElement) return;
    const containerWidth = this.canvasElement.parentElement.clientWidth;
    if (!containerWidth) return;
    const unscaledViewport = page.getViewport({
      scale: 1
    });
    const baseScale = containerWidth / unscaledViewport.width;
    const scale = baseScale * (this.zoomLevel / 100);
    const viewport = page.getViewport({
      scale,
      rotation: this.rotation
    });
    const context = this.canvasElement.getContext('2d');
    this.canvasElement.height = viewport.height;
    this.canvasElement.width = viewport.width;
    context.fillStyle = 'white';
    context.fillRect(0, 0, viewport.width, viewport.height);
    const renderTask = page.render({
      canvasContext: context,
      viewport
    });
    this._currentRenderTask = renderTask;
    try {
      yield renderTask.promise;
    } catch (e) {
      if (e?.name !== 'RenderingCancelledException') {
        throw e;
      }
    } finally {
      if (this._currentRenderTask === renderTask) {
        this._currentRenderTask = null;
      }
    }
  }

  // pdf.js dipinge i valori dei form field sul canvas (genera le appearance al
  // volo), ma il motore di stampa nativo del browser stampa i byte grezzi del
  // PDF: se il file ha i valori in /V senza appearance streams, i valori
  // spariscono in stampa. pdf-lib carica i byte, appiattisce il form
  // (form.flatten() trasforma i campi in contenuto statico di pagina) e
  // rigenera i byte, così ogni renderer mostra i valori.
  //
  // Enqueue: print e download possono partire insieme; il secondo perform
  // aspetta il primo e riusa la cache invece di riappiattire.
  *flattenPdfTask() {
    if (this._flattenedBlob instanceof Blob) return this._flattenedBlob;
    // Snapshot: a restarting loadPdfTask can swap pdfBlob between our yields.
    const sourceBlob = this.pdfBlob;
    if (!(sourceBlob instanceof Blob)) return null;
    try {
      const bytes = yield sourceBlob.arrayBuffer();
      const doc = yield PDFDocument.load(bytes);
      doc.getForm().flatten();
      const flattenedBytes = yield doc.save();
      this._flattenedBlob = new Blob([flattenedBytes], {
        type: 'application/pdf'
      });
      return this._flattenedBlob;
    } catch (e) {
      // No AcroForm, encrypted or malformed: fall back to the original blob so
      // print/download keep working.
      //
      // Encrypted input is NOT a defect and must NOT be "fixed" with
      // `PDFDocument.load(bytes, { ignoreEncryption: true })`, even though the
      // pdf-lib error message suggests it: pdf-lib ships no decryption at all
      // (there is not a single decrypt routine in the package), so that option
      // only skips the guard. It would then parse still-encrypted strings and
      // streams and `doc.save()` would write them back WITHOUT the /Encrypt
      // dictionary — a file every reader treats as corrupt. That would turn a
      // print/download that works today (the original bytes, which the browser
      // print engine and pdf.js both decrypt on their own) into a broken
      // document, on medical and fiscal files. Keeping the original blob is the
      // correct outcome, not a workaround (Sentry EMBER-MEDICAL-2X4).
      //
      // Level: `info` for the encrypted case — expected, handled, unactionable,
      // and `warn` would still reach Sentry via captureMessage. Anything else
      // is an unexpected pdf-lib failure worth seeing, but the user still gets
      // a working file, so it is a `warn` (Sentry warning) and not an
      // exception-level capture.
      if (e instanceof EncryptedPDFError) {
        this.log.info('Encrypted PDF cannot be flattened, using the original blob:', e);
      } else {
        this.log.warn('PDF flatten failed, using the original blob:', e);
      }
      return sourceBlob;
    }
  }
  *printPdfTask() {
    const blob = yield this.flattenPdfTask.perform();
    if (!(blob instanceof Blob)) {
      this.log.error('Invalid blob for printing', blob);
      return;
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
    this.blobUrl = URL.createObjectURL(blob);
    let loaded = false;
    try {
      // Niente modale di Print.js (showModal:false): la sua overlay che appare
      // e sparisce è il "flash" prima del popup di stampa. Il feedback di
      // caricamento lo dà il bottone (printPdfTask.isRunning): tenendo il task
      // in esecuzione fino a onLoadingEnd, lo spinner resta finché l'iframe è
      // pronto, poi parte il dialog nativo. Nessun flash.
      //
      // The race is what bounds that wait: `onError` and the timeout both
      // resolve `false`, so the task always settles and the spinner always
      // stops, whatever print-js does.
      loaded = yield Promise.race([new Promise(resolve => {
        printJS({
          printable: this.blobUrl,
          type: 'pdf',
          showModal: false,
          onLoadingEnd: () => resolve(true),
          onError: e => {
            this.log.error('Print.js reported an error:', e);
            resolve(false);
          }
        });
      }), timeout(PRINT_LOADING_TIMEOUT_MS).then(() => false)]);
    } catch (e) {
      this.log.error('Print.js printing failed:', e);
      loaded = false;
    }
    if (this.isDestroyed || this.isDestroying) return;
    if (!loaded) {
      this._printInBrowserViewer();
    }
  }

  // Fallback when print-js never opened the native dialog: hand the PDF to the
  // browser's own viewer in a new tab, where printing always works. A blocked
  // popup is the only remaining failure, and it is one the user can act on, so
  // it gets a message instead of a silent spinner.
  _printInBrowserViewer() {
    const url = this.blobUrl;
    // NOT `window.open(url, '_blank', 'noopener')`: with `noopener` in the
    // feature string the browser returns null by spec, so the success branch
    // below could never run — the tab opened, the user was told it failed and
    // the blob was revoked from under it. Sever the opener by hand instead
    // (the URL is a same-origin blob, there is no cross-origin page to fear).
    const openedTab = url ? window.open(url, '_blank') : null;
    if (openedTab) {
      try {
        openedTab.opener = null;
      } catch {
        // some in-app browsers expose a sealed proxy: the tab is open anyway
      }
      this._printFallbackAt = Date.now();
      this.notify.info(this.intl.t('pdf.print.opened-in-tab'));
      return;
    }
    this.log.error('Print.js never signalled and the fallback tab was blocked');
    this.notify.error(this.intl.t('pdf.print.failed'));
  }

  // Drop: click rapidi non si accodano, il render in corso completa prima.
  *nextPageTask() {
    if (this.page < this.totalPages) {
      this.page++;
      yield this.loadPageTask.perform();
    }
  }
  *prevPageTask() {
    if (this.page > 1) {
      this.page--;
      yield this.loadPageTask.perform();
    }
  }
  *rotatePdfTask() {
    this.rotation = (this.rotation + 90) % 360;
    yield this.loadPageTask.perform();
  }
  *zoomInTask() {
    const currentIndex = this.zoomOptions.indexOf(this.zoomLevel);
    if (currentIndex < this.zoomOptions.length - 1) {
      this.zoomLevel = this.zoomOptions[currentIndex + 1];
      yield this.loadPageTask.perform();
    }
  }
  *zoomOutTask() {
    const currentIndex = this.zoomOptions.indexOf(this.zoomLevel);
    if (currentIndex > 0) {
      this.zoomLevel = this.zoomOptions[currentIndex - 1];
      yield this.loadPageTask.perform();
    }
  }
  *setZoomFromSelectTask(event) {
    this.zoomLevel = parseInt(event.target.value);
    yield this.loadPageTask.perform();
  }
  *goToPageTask(event) {
    const value = parseInt(event.target.value);
    if (value >= 1 && value <= this.totalPages) {
      this.page = value;
      yield this.loadPageTask.perform();
    }
  }
  registerCanvas(element) {
    this.canvasElement = element;

    // Wait until the container has real dimensions before loading.
    // Modals/panels with CSS animations have clientWidth=0 at did-insert time,
    // which would cause loadPageTask to render the PDF at scale 0 (invisible canvas).
    const container = element.parentElement;
    if (container.clientWidth > 0) {
      this.loadPdfTask.perform();
      return;
    }
    this._resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width > 0) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
        if (!this.isDestroying && !this.isDestroyed) {
          this.loadPdfTask.perform();
        }
      }
    });
    this._resizeObserver.observe(container);
  }
  handleUrlChange() {
    this.loadPdfTask.perform();
  }

  // Drop: download appiattito così il file salvato conserva i valori dei campi,
  // coerente con la stampa.
  *downloadPdfTask() {
    const blob = yield this.flattenPdfTask.perform();
    if (!(blob instanceof Blob)) return;
    const blobUrl = URL.createObjectURL(blob);
    // `url` is a live arg owned by the parent; the flatten above can null it
    // (viewer closed / swapped). It only feeds the fallback filename here.
    const urlParts = this.args.url?.split('/') || [];
    let originalFilename = this.args.downloadName || urlParts[urlParts.length - 1] || 'documento.pdf';
    originalFilename = originalFilename.split('?')[0].split('#')[0];
    const safeFilename = `${originalFilename.replace(/\.[^/.]+$/, '')}.pdf`;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    if (isIOS) {
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } else {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = this.args.downloadPdfName || safeFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }
  }
  get isFirstPage() {
    return this.page <= 1;
  }
  get isLastPage() {
    return this.page >= this.totalPages;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "log", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "canvasElement", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "pdf", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "pdfBlob", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "blobUrl", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "fallbackMode", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "loadError", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "unreadableDocument", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "page", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 1;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "totalPages", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 1;
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "zoomLevel", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 100;
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "rotation", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "loadPdfTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "loadPdfTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loadPageTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "loadPageTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "flattenPdfTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "flattenPdfTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "printPdfTask", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "printPdfTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "nextPageTask", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "nextPageTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "prevPageTask", [_dec6], Object.getOwnPropertyDescriptor(_class.prototype, "prevPageTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rotatePdfTask", [_dec7], Object.getOwnPropertyDescriptor(_class.prototype, "rotatePdfTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "zoomInTask", [_dec8], Object.getOwnPropertyDescriptor(_class.prototype, "zoomInTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "zoomOutTask", [_dec9], Object.getOwnPropertyDescriptor(_class.prototype, "zoomOutTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setZoomFromSelectTask", [_dec0], Object.getOwnPropertyDescriptor(_class.prototype, "setZoomFromSelectTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "goToPageTask", [_dec1], Object.getOwnPropertyDescriptor(_class.prototype, "goToPageTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerCanvas", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerCanvas"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleUrlChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleUrlChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "downloadPdfTask", [_dec10], Object.getOwnPropertyDescriptor(_class.prototype, "downloadPdfTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbPdfViewerComponent);

export { BbPdfViewerComponent as default };
//# sourceMappingURL=bb-pdf-viewer.js.map
